import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Picture from '../components/Picture';
import AdminNav from '../components/AdminNav';
import logo from '../assets/images/logoFree.webp';
import logoFallback from '../assets/images/logoFree.png';
import '../styles/AdminLogsPage.css';
import '../styles/AdminRealisableCardsPage.css';
import {
    ThumbsUpIcon,
    XCircleIcon,
    TrashSimpleIcon
} from '@phosphor-icons/react';
import { getAdminKey } from '../utils/adminAuth';

const API_ENDPOINT = '/.netlify/functions/realizable-cards';
const ITEMS_PER_PAGE = 32;
const SEARCH_LANG = 'fr';
const STATUS_LABELS = {
    realizable: 'Réalisable',
    blocked: 'Non réalisable',
    neutral: 'Neutre'
};

const buildImageUrl = (uri) => (uri ? `${uri}/high.webp` : '');

const mergeStatusEntry = (entries, cardId, data) => {
    const index = entries.findIndex((entry) => entry.card_id === cardId);
    const baseEntry = index >= 0 ? entries[index] : null;
    const updatedEntry = {
        card_id: cardId,
        ...(baseEntry || {}),
        ...data
    };
    const remaining =
        index >= 0
            ? [...entries.slice(0, index), ...entries.slice(index + 1)]
            : entries;
    return [updatedEntry, ...remaining];
};

function AdminRealisableCardsPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [statusEntries, setStatusEntries] = useState([]);
    const [statusLoading, setStatusLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [manualId, setManualId] = useState('');
    const [manualStatus, setManualStatus] = useState('realizable');
    const [currentPage, setCurrentPage] = useState(1);
    const [lastSearchTerm, setLastSearchTerm] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const location = useLocation();
    const isAdmin = Boolean(getAdminKey());
    const statusMap = useMemo(
        () => new Map(statusEntries.map((entry) => [entry.card_id, entry.status])),
        [statusEntries]
    );

    const fetchStatuses = useCallback(async () => {
        setStatusLoading(true);
        try {
            const response = await fetch(API_ENDPOINT);
            if (!response.ok) {
                throw new Error('Impossible de charger les statuts.');
            }
            const payload = await response.json();
            setStatusEntries(
                (Array.isArray(payload) ? payload : []).map((row) => ({
                    card_id: row.card_id,
                    status: row.status || 'neutral',
                    image_path: row.image_path || null
                }))
            );
        } catch (error) {
            setFeedback({ type: 'error', text: error.message });
        } finally {
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatuses();
    }, [fetchStatuses]);

    const updateStatus = useCallback(
        async (cardId, status) => {
            setStatusLoading(true);
            setFeedback(null);
            const previousEntries = statusEntries;
            setStatusEntries((prev) => mergeStatusEntry(prev, cardId, { status }));
            try {
                const response = await fetch(API_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cardId, status })
                });
                if (!response.ok) {
                    throw new Error('Impossible de mettre à jour la carte.');
                }
                await fetchStatuses();
                setFeedback({
                    type: 'success',
                    text: `Carte ${cardId} enregistrée en ${STATUS_LABELS[status]}.`
                });
            } catch (error) {
                setStatusEntries(previousEntries);
                setFeedback({ type: 'error', text: error.message });
            } finally {
                setStatusLoading(false);
            }
        },
        [fetchStatuses, statusEntries]
    );

    const removeStatus = useCallback(
        async (cardId) => {
            setStatusLoading(true);
            setFeedback(null);
            try {
                const response = await fetch(API_ENDPOINT, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cardId })
                });
                if (!response.ok) {
                    throw new Error('Impossible de supprimer la carte.');
                }
                await fetchStatuses();
                setFeedback({
                    type: 'success',
                    text: `Carte ${cardId} supprimée de la liste.`
                });
            } catch (error) {
                setFeedback({ type: 'error', text: error.message });
            } finally {
                setStatusLoading(false);
            }
        },
        [fetchStatuses]
    );

    const fetchSearchPage = async (term, page = 1, append = false) => {
        const trimmed = term.trim();
        if (!trimmed) {
            setSearchResults([]);
            setSearchError('');
            setHasMore(false);
            return;
        }
        const url = new URL(`https://api.tcgdex.net/v2/${SEARCH_LANG}/cards`);
        url.searchParams.set('name', `like:${trimmed}`);
        url.searchParams.set('pagination:itemsPerPage', ITEMS_PER_PAGE.toString());
        url.searchParams.set('pagination:page', page.toString());

        if (!append) {
            setSearchResults([]);
            setCurrentPage(1);
        }
        setSearchError('');
        setSearchLoading(true);
        try {
            const response = await fetch(url.toString());
            if (!response.ok) {
                throw new Error('Recherche tcgdex échouée.');
            }
            const data = await response.json();
            const payload = Array.isArray(data) ? data : [];
            const cardsWithImages = payload.filter((card) => card.image);
            const formatted = cardsWithImages.map((card) => ({
                card,
                imageUrl: buildImageUrl(card.image)
            }));
            setSearchResults((prev) => (append ? [...prev, ...formatted] : formatted));
            setCurrentPage(page);
            setLastSearchTerm(trimmed);
            setHasMore(payload.length === ITEMS_PER_PAGE);
        } catch (error) {
            setSearchError(error.message);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearch = (event) => {
        event.preventDefault();
        fetchSearchPage(searchTerm, 1, false);
    };

    const handleLoadMore = () => {
        if (!hasMore || !lastSearchTerm || searchLoading) {
            return;
        }
        fetchSearchPage(lastSearchTerm, currentPage + 1, true);
    };

    const handleManualChange = (event) => {
        event.preventDefault();
        const trimmed = manualId.trim();
        if (!trimmed) {
            return;
        }
        updateStatus(trimmed, manualStatus);
        setManualId('');
    };

    const heroRealisable = useMemo(
        () => statusEntries.filter((entry) => entry.status === 'realizable').length,
        [statusEntries]
    );
    const statsSummary = useMemo(() => {
        const blocked = statusEntries.filter((entry) => entry.status === 'blocked').length;
        return {
            total: statusEntries.length,
            realizable: heroRealisable > 0 ? heroRealisable : Math.max(0, statusEntries.length - blocked),
            blocked
        };
    }, [heroRealisable, statusEntries]);
    const heroRealisableCount = heroRealisable > 0 ? heroRealisable : Math.max(0, statsSummary.total - statsSummary.blocked);

    const statusLegend = useMemo(
        () => (
            <div className='admin-status-legend'>
                <span>
                    <ThumbsUpIcon size={16} weight='fill' /> : réalisable
                </span>
                <span>
                    <XCircleIcon size={16} weight='fill' /> : non réalisable
                </span>
            </div>
        ),
        []
    );

    const statusCards = useMemo(
        () =>
            statusEntries.map((entry) => {
                const status = entry.status || 'neutral';
                const preview = entry.image_path ? `${entry.image_path}/low.webp` : null;
                const toggleTarget = status === 'realizable' ? 'blocked' : 'realizable';
                const ToggleIcon = status === 'realizable' ? XCircleIcon : ThumbsUpIcon;
                const toggleLabel =
                    status === 'realizable'
                        ? 'Marquer non réalisable'
                        : 'Marquer réalisable';
                return (
                    <article key={entry.card_id} className='admin-status-card'>
                        <div className='admin-status-thumb'>
                            {preview ? (
                                <img src={preview} alt={`Aperçu ${entry.card_id}`} />
                            ) : (
                                <div className='admin-status-placeholder'>
                                    <span>?</span>
                                </div>
                            )}
                            <div className='admin-status-overlay'>
                                <button
                                    type='button'
                                    className='icon-button icon-button-small'
                                    aria-label={toggleLabel}
                                    onClick={() => updateStatus(entry.card_id, toggleTarget)}
                                >
                                    <ToggleIcon size={16} weight='fill' />
                                </button>
                                <button
                                    type='button'
                                    className='icon-button icon-button-small'
                                    aria-label='Supprimer le statut'
                                    onClick={() => removeStatus(entry.card_id)}
                                >
                                    <TrashSimpleIcon size={16} weight='fill' />
                                </button>
                            </div>
                        </div>
                        <div className='admin-status-card-meta'>
                            <span className='admin-card-id'>{entry.card_id}</span>
                            <span className={`status-pill ${status}`}>
                                {STATUS_LABELS[status]}
                            </span>
                        </div>
                    </article>
                );
            }),
        [removeStatus, statusEntries, updateStatus]
    );
    if (!isAdmin) {
        return (
            <Navigate
                to='/admin'
                replace
                state={{
                    from: location
                }}
            />
        );
    }

    return (
        <div className='page admin-page'>
            <div className='admin-nav-shell'>
                <AdminNav />
            </div>
            <main>
                <section className='hero admin-hero'>
                    <div className='container admin-hero-inner'>
                        <div className='admin-hero-copy'>
                            <div className='admin-brand'>
                                <Picture
                                    webpSrc={logo}
                                    fallbackSrc={logoFallback}
                                    alt='MAF Industries'
                                    className='admin-brand-logo'
                                    imgClassName='admin-brand-logo-img'
                                />
                                <div>
                                    <span className='admin-brand-title'>
                                        MAF Industries
                                    </span>
                                    <span className='admin-brand-subtitle'>
                                        Cartes 3D - espace admin
                                    </span>
                                </div>
                            </div>
                            <h1>Gestion des cartes réalisables</h1>
                            <p className='lead'>
                                Suivi des statuts de cartes 3D, mises à jour rapides et priorisation des projets faisables.
                            </p>
                            <div className='admin-hero-actions admin-hero-actions--stats'>
                                <div className='admin-hero-pill'>
                                    <strong>{statsSummary.total}</strong>
                                    <span>cartes suivies</span>
                                </div>
                                <div className='admin-hero-pill'>
                                    <strong>{heroRealisableCount}</strong>
                                    <span>réalisables</span>
                                </div>
                                <div className='admin-hero-pill'>
                                    <strong>{statsSummary.blocked}</strong>
                                    <span>bloquées</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div className='container'>
                    <section className='admin-panel admin-realisable-panel'>
                        <header className='admin-panel-header'>
                            <h2>Recherche et mise à jour</h2>
                            <p>
                                Recherche par nom, visualise les cartes et marque/bloque celles que tu peux ou ne peux pas réaliser.
                            </p>
                        </header>
                        <form className='admin-search-form' onSubmit={handleSearch}>
                            <div className='admin-search-field'>
                                <input
                                    type='text'
                                    placeholder='Ex: Charizard, Pikachu...'
                                    value={searchTerm}
                                    className='admin-search-input'
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                />
                                <button type='submit' className='admin-search-button' disabled={searchLoading}>
                                    Rechercher
                                </button>
                            </div>
                        </form>
                        {searchError && <p className='admin-note'>{searchError}</p>}
                        {searchLoading && <p className='admin-note'>Recherche en cours…</p>}
                        <div className='admin-search-grid'>
                            {searchResults.map(({ card, imageUrl }) => {
                                const status = statusMap.get(card.id) || 'neutral';
                                return (
                                    <article key={card.id} className='admin-search-card'>
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={card.name} />
                                        ) : (
                                            <div className='admin-search-placeholder'>
                                                <span>Pas d’image</span>
                                            </div>
                                        )}
                                        <div className='admin-search-info'>
                                            <strong>{card.name}</strong>
                                            <span className='admin-card-id'>{card.id}</span>
                                            <span className={`status-pill ${status}`}>
                                                {STATUS_LABELS[status]}
                                            </span>
                                        </div>
                                        <div className='admin-card-actions'>
                                            <button
                                                type='button'
                                                className='icon-button'
                                                aria-label='Marquer réalisable'
                                                disabled={status === 'realizable'}
                                                onClick={() => updateStatus(card.id, 'realizable')}
                                            >
                                                <ThumbsUpIcon size={18} weight='fill' />
                                            </button>
                                            <button
                                                type='button'
                                                className='icon-button'
                                                aria-label='Marquer non réalisable'
                                                disabled={status === 'blocked'}
                                                onClick={() => updateStatus(card.id, 'blocked')}
                                            >
                                                <XCircleIcon size={18} weight='fill' />
                                            </button>
                                            <button
                                                type='button'
                                                className='icon-button'
                                                aria-label='Réinitialiser'
                                                onClick={() => removeStatus(card.id)}
                                            >
                                                <TrashSimpleIcon size={18} weight='fill' />
                                            </button>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                        {hasMore && (
                            <div className='admin-load-more'>
                                <button
                                    type='button'
                                    className='admin-load-button'
                                    onClick={handleLoadMore}
                                    disabled={searchLoading}
                                >
                                    Charger plus
                                </button>
                            </div>
                        )}
                        <form className='admin-search-form' onSubmit={handleManualChange}>
                            <label htmlFor='manual-card-id'>Ajout manuel</label>
                            <div className='admin-cards-form-row'>
                                <input
                                    id='manual-card-id'
                                    type='text'
                                    placeholder='dp3-3'
                                    value={manualId}
                                    onChange={(event) => setManualId(event.target.value)}
                                />
                                <select value={manualStatus} onChange={(event) => setManualStatus(event.target.value)}>
                                    <option value='realizable'>Réalisable</option>
                                    <option value='blocked'>Non réalisable</option>
                                    <option value='neutral'>Neutre</option>
                                </select>
                                <button type='submit' className='button button-outline'>
                                    Appliquer
                                </button>
                            </div>
                        </form>
                        {feedback && <p className={`admin-note admin-note-${feedback.type}`}>{feedback.text}</p>}
                        {statusLegend}
                        {statusLoading && <p className='admin-note'>Mise à jour en cours…</p>}
                        <div className='admin-status-panel'>
                            {statusEntries.length === 0 ? (
                                <p>Aucun statut enregistré pour le moment.</p>
                            ) : (
                                <div className='admin-status-grid'>{statusCards}</div>
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}

export default AdminRealisableCardsPage;
